<template>
  <div class="relative" :class="cssClasses">
    <LineChartGenerator
      data-testid="simple-line-chart"
      :options="options"
      :data="chartData"
      :plugins="plugins"
      :styles="computedStyles"
      :width="width"
      :height="height"
    />
    <BenchmarkLogoBlack
      class="absolute pointer-events-none z-[5] top-1/2 -translate-y-[60%] left-1/2 -translate-x-1/2 w-[65%] h-[65%] opacity-10"
    />
  </div>
</template>

<script setup lang="ts">
import CrosshairPlugin from 'chartjs-plugin-crosshair'
import { Line as LineChartGenerator } from 'vue-chartjs'
import resolveConfig from 'tailwindcss/resolveConfig'
import { numberFormatter } from '@/utils/number'
import { reformat } from '~/utils/date'
import { logError } from '~/utils/log-error'
import BenchmarkLogoBlack from '~/assets/icons/BenchmarkLogoBlack.svg'

import tailwindConfig from '~/tailwind.config'

const fullConfig = resolveConfig(tailwindConfig)
const emit = defineEmits(['onHover'])

const plugins = [CrosshairPlugin]
const props = defineProps({
  chartData: {
    type: Object,
    default: () => {},
  },
  width: {
    type: Number,
    default: 400,
  },
  height: {
    type: Number,
    default: 400,
  },
  cssClasses: {
    default: '',
    type: String,
  },
  styles: {
    type: Object,
    default: () => {},
  },
  yAxisLabel: {
    type: String,
    required: false,
  },
  isMobile: {
    type: Boolean,
    required: true,
  },
  minY: {
    type: Number,
    default: null,
  },
  maxY: {
    type: Number,
    default: null,
  },
  tooltipValueSymbol: {
    type: String,
    default: '',
  },
})

const options: any = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  elements: {
    line: {
      tension: 0, // disables bezier curves
      borderWidth: props.isMobile ? 1 : 3.5,
    },
    point: {
      radius: 0,
    },
  },
  onHover: (event) => {
    emit('onHover', event)
  },
  scales: {
    y: {
      display: !props.isMobile,
      position: 'left',
      min: props.minY,
      max: props.maxY,
      ticks: {
        maxTicksLimit: 7,
        font: {
          family: 'Montserrat',
          size: 10,
          color: fullConfig.theme.colors.s['900'],
          weight: '500',
        },
        padding: 0,
        beginAtZero: true,
        callback(value) {
          return value > 0 ? `${numberFormatter(value)}` : value
        },
      },
      grid: {
        drawOnChartArea: false,
      },
    },
    x: {
      bounds: 'ticks',
      ticks: {
        maxRotation: 0,
        autoSkip: true,
        autoSkipPadding: 32,
        maxTicksLimit: 7,
        font: {
          family: 'Montserrat',
          size: props.isMobile ? 7.5 : 10,
          color: fullConfig.theme.colors.s['900'],
          weight: props.isMobile ? '600' : '500',
        },
        padding: 0,
        callback: function (value) {
          const label = this.getLabelForValue(value)
          return reformat(label, 'dd/MM/yyyy')
        },
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
  plugins: {
    crosshair: {
      line: {
        color: fullConfig.theme.colors.s['500'],
        dashPattern: [2, 2],
        width: 1,
      },
      zoom: {
        enabled: false,
      },
      snap: {
        enabled: true,
      },
      sync: {
        enabled: false,
      },
    },
    legend: {
      display: false,
    },
    tooltip: {
      boxWidth: 4.5,
      boxHeight: 17,
      boxPadding: 0,
      enabled: !props.isMobile,
      mode: 'nearest',
      intersect: false,
      titleFont: {
        family: 'Montserrat',
        size: 14,
      },
      bodyFont: {
        family: 'Montserrat',
        size: 12,
      },
      multiKeyBackground: '#00000000',
      bodySpacing: 8,
      padding: {
        x: 12,
        y: 8,
      },
      callbacks: {
        label(context) {
          const { dataIndex, dataset } = context
          const { label, digits } = context.dataset
          const labelReplaceFirst = label.replace(/[*]+/g, '').trim()
          return `${labelReplaceFirst}: ${props.tooltipValueSymbol}${numberFormatter(
            dataset.data[dataIndex].y,
            digits,
          )}`
        },
        labelColor(context) {
          return {
            backgroundColor: context.dataset.borderColor,
          }
        },
        title(tooltipItems) {
          const { dataIndex } = tooltipItems[0]
          const tooltipItem = tooltipItems[0].dataset.data[dataIndex]
          const date = tooltipItem.x

          try {
            return reformat(date, 'MMM dd, yyyy')
          } catch (e) {
            logError(e)
          }
        },
      },
    },
  },
}))

const computedStyles = computed(() => ({
  ...props.styles,
  height: `${props.height}px`,
  width: `${props.width}px`,
}))
</script>
